import React, { useEffect, useMemo, useState } from 'react';
import { Play, PlayCircle } from 'lucide-react';
import toolkitService from '../services/ToolkitService';
import Button from './shared/Button';
import { useToolkitProject, ToolkitProjectHeader } from './toolkit/ToolkitProject';
import { STEP, stepIcon } from './toolkit/ToolkitFileStep';
import ToolkitCompareResults from './toolkit/ToolkitCompareResults';
import useOnPrem from './toolkit/useOnPrem';
import './OhipTransactionsPage.css';
import './ToolkitBlocksPage.css';

// Sync › project › Toolkit › v5 vs Cloud Config — the desktop's Scripts › Opera v5 vs Opera Cloud.
//
// Per check: the v5 configuration table is read through the project's SyncConnect (catalogue key config-<check>), then
// the toolkit lambda reads the matching Opera Cloud list through the project's connection and compares the codes
// (toolkit configcompare.js). Every v5 code is matched, inactive in Cloud, or missing in Cloud — the gaps to configure.
// Read-only on both sides.

const STATUSES = [
    { value: 'missing', label: 'Missing in Cloud' },
    { value: 'inactive', label: 'Inactive in Cloud' },
    { value: 'match', label: 'Matched' },
];
const COLUMNS = [
    { key: 'code', label: 'Code' },
    { key: 'description', label: 'v5 description' },
    { key: 'cloudDescription', label: 'Cloud description' },
];

function ToolkitConfigComparePage() {
    const toolkit = useToolkitProject();
    const { ctx, ready } = toolkit;
    const { ask } = useOnPrem(ctx);
    const [checks, setChecks] = useState([]);
    const [area, setArea] = useState('');
    const [runs, setRuns] = useState({});        // key → { state, message, result }
    const [shown, setShown] = useState('');
    const [runningAll, setRunningAll] = useState(false);

    useEffect(() => {
        if (!ready) return;
        toolkitService.onprem(ctx, 'config-checks').then(({ data }) => {
            setChecks(data.items || []);
            setArea((a) => a || data.items?.[0]?.area || '');
        }).catch(() => {});
    }, [ready, ctx]);

    const areas = useMemo(() => [...new Set(checks.map((c) => c.area))], [checks]);
    const inArea = checks.filter((c) => c.area === area);
    const set = (key, v) => setRuns((r) => ({ ...r, [key]: { ...r[key], ...v } }));

    const run = async (c) => {
        set(c.key, { state: STEP.BUSY, message: 'Asking SyncConnect for the v5 values…', result: null });
        try {
            const d = await ask(`config-${c.key}`);
            if (d.status !== 'RETURNED') { set(c.key, { state: STEP.ERROR, message: d.error_message || `No answer yet (${d.status}).` }); return; }
            set(c.key, { message: 'Reading Opera Cloud and comparing…' });
            const { data } = await toolkitService.onprem(ctx, 'config-compare', { check: c.key, queryId: d.id });
            const s = data.summary;
            set(c.key, { state: s.missing ? STEP.ERROR : STEP.DONE, result: data,
                message: `${s.matched} matched, ${s.inactive} inactive, ${s.missing} missing of ${s.v5Total} v5 code(s).` });
            setShown(c.key);
        } catch (e) {
            set(c.key, { state: STEP.ERROR, message: e?.response?.data?.message || e.message });
        }
    };

    const runArea = async () => {
        setRunningAll(true);
        for (const c of inArea) await run(c);      // one at a time: SyncConnect and OHIP both throttle
        setRunningAll(false);
    };

    const current = runs[shown]?.result;

    return (
        <div className="otx-page">
            <ToolkitProjectHeader toolkit={toolkit} title="v5 vs Cloud Config"
                subtitle="Compare the property's Opera v5 configuration codes with Opera Cloud, and list what is missing." />

            <div className="tkb-tabs">
                {areas.map((a) => (
                    <button key={a} type="button" className={`tkb-tab${a === area ? ' tkb-tab-active' : ''}`} onClick={() => setArea(a)}>{a}</button>
                ))}
            </div>

            <div className="tkb-step">
                <div className="tkb-step-head" style={{ display: 'flex', alignItems: 'center' }}>
                    {area} checks
                    <div className="tkb-toolbar-spacer" />
                    <Button variant="secondary" onClick={runArea} disabled={!ready || runningAll || !inArea.length}>
                        <PlayCircle className="icon" /> Run all in {area || 'area'}
                    </Button>
                </div>
                <div className="otx-table-wrap">
                    <table className="otx-table">
                        <thead><tr><th>Check</th><th>v5 table</th><th>Result</th><th /></tr></thead>
                        <tbody>{inArea.map((c) => {
                            const r = runs[c.key] || {};
                            return (
                                <tr key={c.key} style={shown === c.key ? { background: '#eef2ff' } : undefined}>
                                    <td>{c.title}</td>
                                    <td><code>{c.table}</code></td>
                                    <td className={r.state === STEP.ERROR ? 'otx-fail' : ''}>{stepIcon(r.state)} {r.message}</td>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        <Button variant="secondary" onClick={() => run(c)} disabled={!ready || r.state === STEP.BUSY || runningAll}>
                                            <Play className="icon" /> Run
                                        </Button>
                                        {r.result && <Button variant="secondary" onClick={() => setShown(c.key)} style={{ marginLeft: 6 }}>Show</Button>}
                                    </td>
                                </tr>
                            );
                        })}</tbody>
                    </table>
                </div>
            </div>

            {current && (
                <div className="tkb-step">
                    <div className="tkb-step-head">{current.title}</div>
                    <ToolkitCompareResults key={shown} rows={current.rows} columns={COLUMNS} statuses={STATUSES}
                        exportName={`Config_${current.check}`}
                        summary={[
                            { label: 'v5 codes', value: current.summary.v5Total },
                            { label: 'Cloud codes', value: current.summary.cloudTotal },
                            { label: 'Matched', value: current.summary.matched, tone: 'ok' },
                            { label: 'Inactive in Cloud', value: current.summary.inactive, tone: current.summary.inactive ? 'warn' : undefined },
                            { label: 'Missing in Cloud', value: current.summary.missing, tone: current.summary.missing ? 'fail' : 'ok' },
                        ]}
                        note="Codes only in Opera Cloud are not listed — only the v5 codes Cloud lacks are gaps." />
                </div>
            )}
        </div>
    );
}

export default ToolkitConfigComparePage;
